import type { AthleteProfile, FtpPolicySelection, PowerZone } from "./types/athleteProfile";

export const ATHLETE_PROFILE_SCHEMA_VERSION = 4;

function nowIso(): string {
  return new Date().toISOString();
}

function createPowerZones(ftpWatts: number): PowerZone[] {
  const zoneDefinitions: Array<{ name: string; minFraction: number; maxFraction: number | null }> = [
    { name: "Z1 Recovery", minFraction: 0, maxFraction: 0.55 },
    { name: "Z2 Endurance", minFraction: 0.56, maxFraction: 0.75 },
    { name: "Z3 Tempo", minFraction: 0.76, maxFraction: 0.9 },
    { name: "Z4 Threshold", minFraction: 0.91, maxFraction: 1.05 },
    { name: "Z5 VO2", minFraction: 1.06, maxFraction: 1.2 },
    { name: "Z6 Anaerobic", minFraction: 1.21, maxFraction: 1.5 },
    { name: "Z7 Neuromuscular", minFraction: 1.51, maxFraction: null }
  ];

  return zoneDefinitions.map(zone => ({
    name: zone.name,
    minWatts: Math.round(ftpWatts * zone.minFraction),
    maxWatts: zone.maxFraction === null ? null : Math.round(ftpWatts * zone.maxFraction)
  }));
}

export function createSeedAthleteProfile(): AthleteProfile {
  const defaultCurrentFtp = 320;
  const defaultOutdoorFtp = 325;
  const defaultIndoorFtp = 315;
  const defaultRoadFtp = 325;
  const defaultGravelFtp = 315;
  const defaultAnalysisPolicy: FtpPolicySelection = { kind: "athlete-profile-default", manualFtpWatts: null };
  const effectiveDateIso = nowIso();

  return {
    personal: {
      id: "athlete-sam",
      firstName: "Sam",
      lastName: "",
      dateOfBirth: null,
      heightInches: 69,
      currentWeightLb: 182.6,
      targetWeightLb: {
        minimum: 170,
        maximum: 175
      },
      preferredUnits: "imperial"
    },
    performance: {
      ftpWatts: defaultCurrentFtp,
      ftpEffectiveDateIso: effectiveDateIso,
      ftpSource: "athlete profile",
      indoorFtpWatts: defaultIndoorFtp,
      outdoorFtpWatts: defaultOutdoorFtp,
      roadFtpWatts: defaultRoadFtp,
      gravelFtpWatts: defaultGravelFtp,
      defaultAnalysisFtpPolicy: defaultAnalysisPolicy,
      ftpHistory: [
        {
          effectiveDateIso,
          ftpWatts: defaultCurrentFtp,
          source: "athlete profile",
          policyKind: "athlete-profile-default",
          note: "Seeded profile default"
        }
      ],
      vo2Max: null,
      maxHeartRateBpm: 186,
      restingHeartRateBpm: 46,
      thresholdHeartRateBpm: 170,
      preferredCadenceRpm: 88,
      primaryDiscipline: "gravel",
      powerZones: createPowerZones(defaultCurrentFtp),
      heartRateZones: []
    },
    training: {
      weeklyTrainingHours: 12,
      weeklyTssTarget: 600,
      currentTrainingPhase: "Base",
      raceCalendar: []
    },
    goals: {
      primaryObjective: "Build durable gravel race fitness for spring target event",
      primaryEventName: "UNBOUND Gravel 200",
      primaryEventDate: "2027-05-29",
      eventPriority: "a",
      targetFinishGoal: "Strong, steady execution across 200 miles",
      bodyCompositionGoal: "Reach race-ready composition while preserving power output"
    },
    nutrition: {
      dailyCalories: 3350,
      proteinTargetGrams: 190,
      defaultCarbohydrateTargetGrams: 470,
      defaultFatTargetGrams: 108,
      dailySodiumTargetMg: 3000,
      dailyFluidTargetMl: 3000,
      preferredRideCarbsPerHour: 80,
      preferredSodiumMgPerHour: 1000,
      preferredFluidsMlPerHour: 828,
      nutritionNotes: "Use mixed glucose-fructose fueling for long endurance sessions."
    },
    recovery: {
      preferredSleepHours: 8,
      morningColdPlungeEnabled: true,
      morningColdPlungeTemperatureF: 44,
      morningColdPlungeDurationMinutes: 4,
      morningColdPlungeTiming: "immediately-after-waking",
      contrastTherapyEnabled: false,
      recoveryNotes: "Prioritize morning mobility after cold exposure."
    },
    medicalAndConsiderations: {
      currentMedications: [],
      injuryHistory: "",
      bloodworkNotes: "",
      compressionPreference: "optional",
      recoveryConsiderations: "",
      medicalNotes: ""
    },
    preferences: {
      preferredRideStartTime: "06:30",
      indoorOutdoorPreference: "outdoor",
      weatherPreference: "any"
    }
  };
}

export function buildPowerZonesFromFtp(ftpWatts: number): PowerZone[] {
  return createPowerZones(ftpWatts);
}
